import React, { useState } from 'react';
import propTypes from 'prop-types';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Button, Table, Alert, Progress, Input, Label, FormGroup,
} from 'reactstrap';
import {
  ACTIONS, fetchVouchersByGroup, buildPreview, runManage,
} from '../../../utils/voucherBulkManage';

const STEP = { PICK: 'pick', LOADING: 'loading', PREVIEW: 'preview', RUNNING: 'running', DONE: 'done' };
const LABEL = { activate: 'Activate', deactivate: 'Deactivate', delete: 'Delete (permanent)' };

export default function ModalBulkVoucherManage(props) {
  const { isOpen, onClose, onCompleted } = props;
  const [step, setStep] = useState(STEP.PICK);
  const [group, setGroup] = useState('');
  const [action, setAction] = useState(ACTIONS.DEACTIVATE);
  const [vouchers, setVouchers] = useState([]);
  const [rows, setRows] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState([]);
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');

  const reset = () => {
    setStep(STEP.PICK); setVouchers([]); setRows([]); setResults([]);
    setProgress({ done: 0, total: 0 }); setConfirmText(''); setError('');
  };
  const close = () => { reset(); setGroup(''); onClose(); };

  const loadPreview = async () => {
    const g = group.trim();
    if (!g) { setError('Enter a group_name.'); return; }
    setError(''); setStep(STEP.LOADING); setProgress({ done: 0, total: 0 });
    try {
      const vs = await fetchVouchersByGroup(g);
      if (!vs.length) { setError(`No vouchers found in group "${g}".`); setStep(STEP.PICK); return; }
      const pr = await buildPreview(vs, action, (done, total) => setProgress({ done, total }));
      setVouchers(vs); setRows(pr); setConfirmText(''); setStep(STEP.PREVIEW);
    } catch (err) {
      setError(`Could not load: ${(err && err.message) || 'request failed'}`);
      setStep(STEP.PICK);
    }
  };

  const actionable = rows.filter((r) => !r.blocked);
  const isDelete = action === ACTIONS.DELETE;
  const armed = !isDelete || confirmText.trim() === group.trim();

  const execute = async () => {
    setStep(STEP.RUNNING); setProgress({ done: 0, total: actionable.length });
    const res = await runManage(vouchers, rows, action, (done, total) => setProgress({ done, total }));
    setResults(res); setStep(STEP.DONE);
    if (onCompleted) onCompleted();
  };

  return (
    <Modal isOpen={isOpen} toggle={close} size="lg">
      <ModalHeader toggle={close}>Bulk Manage Vouchers by Group</ModalHeader>
      <ModalBody>
        {step === STEP.PICK && (
          <div>
            <FormGroup>
              <Label>Group name</Label>
              <Input value={group} onChange={(e) => setGroup(e.target.value)} placeholder="e.g. RAMADAN25" />
            </FormGroup>
            <FormGroup>
              <Label>Action</Label>
              <Input type="select" value={action} onChange={(e) => setAction(e.target.value)}>
                <option value={ACTIONS.DEACTIVATE}>Deactivate (reversible)</option>
                <option value={ACTIONS.ACTIVATE}>Activate</option>
                <option value={ACTIONS.DELETE}>Delete (permanent, cannot be undone)</option>
              </Input>
            </FormGroup>
            {action === ACTIONS.DELETE && (
              <Alert color="danger">Delete permanently removes the vouchers. There is no undo. Vouchers already used in a transaction are blocked automatically.</Alert>
            )}
            {error && <Alert color="danger">{error}</Alert>}
          </div>
        )}

        {step === STEP.LOADING && (
          <div>
            <p>Loading group and checking usage {progress.done}/{progress.total}...</p>
            <Progress value={progress.total ? (progress.done / progress.total) * 100 : 0} />
          </div>
        )}

        {step === STEP.PREVIEW && (
          <div>
            <p><strong>{LABEL[action]}</strong> &mdash; group <strong>{group.trim()}</strong>, {rows.length} voucher(s) matched, {actionable.length} will change.</p>
            <Table size="sm" bordered responsive>
              <thead>
                <tr><th>Code</th><th>Name</th><th>Now</th>{isDelete && <th>Redemptions</th>}<th>Result</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={r.blocked ? 'text-muted' : ''}>
                    <td>{r.code}</td>
                    <td>{r.name}</td>
                    <td>{r.currentActive ? 'active' : 'inactive'}</td>
                    {isDelete && <td>{r.txUsages == null ? '-' : r.txUsages}{r.usages ? ` (${r.usages} assigned)` : ''}</td>}
                    <td>{r.blocked ? `skip: ${r.blockReason}` : LABEL[action]}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {isDelete && actionable.length > 0 && (
              <FormGroup>
                <Label>Type the group name <code>{group.trim()}</code> to confirm permanent deletion</Label>
                <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
              </FormGroup>
            )}
            {actionable.length === 0 && <Alert color="warning">Nothing to do, every matched voucher is skipped.</Alert>}
          </div>
        )}

        {step === STEP.RUNNING && (
          <div>
            <p>{LABEL[action]} {progress.done}/{progress.total}...</p>
            <Progress color={isDelete ? 'danger' : 'primary'} value={progress.total ? (progress.done / progress.total) * 100 : 0} />
          </div>
        )}

        {step === STEP.DONE && (
          <div>
            <Alert color={results.some((r) => r.status === 'failed') ? 'danger' : 'success'}>
              {results.filter((r) => r.status === 'ok').length} voucher(s) {action === ACTIONS.DELETE ? 'deleted' : (action === ACTIONS.ACTIVATE ? 'activated' : 'deactivated')}.
              {results.some((r) => r.status === 'failed') && ' Stopped on a failure.'}
            </Alert>
            <Table size="sm" bordered responsive>
              <thead><tr><th>Code</th><th>Status</th><th>Note</th></tr></thead>
              <tbody>
                {results.map((r, i) => (<tr key={i}><td>{r.code}</td><td>{r.status}</td><td>{r.error || ''}</td></tr>))}
              </tbody>
            </Table>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {step === STEP.PICK && <Button color="primary" onClick={loadPreview}>Preview</Button>}
        {step === STEP.PREVIEW && (
          <Button color={isDelete ? 'danger' : 'primary'} disabled={!actionable.length || !armed} onClick={execute}>
            {LABEL[action]} {actionable.length} voucher(s)
          </Button>
        )}
        {step === STEP.PREVIEW && <Button color="secondary" onClick={reset}>Back</Button>}
        {step === STEP.DONE && <Button color="secondary" onClick={reset}>Do another</Button>}
        <Button color="link" onClick={close} disabled={step === STEP.RUNNING}>Close</Button>
      </ModalFooter>
    </Modal>
  );
}

ModalBulkVoucherManage.propTypes = {
  isOpen: propTypes.bool,
  onClose: propTypes.func.isRequired,
  onCompleted: propTypes.func,
};
ModalBulkVoucherManage.defaultProps = { isOpen: false };
